import React from 'react';
import { ArrowRight, Calendar, Heart, Tag } from 'lucide-react';
import { Button } from '../components/Button.jsx';
import Badge from '../components/Badge.jsx';
import PublicContainer from './PublicContainer.jsx';

const DEFAULT_SUBTITLE = 'Connect with trusted local businesses and services in your community. From dining to shopping, find everything you need right here.';

export default function PublicHeroSection({
  spotlightName,
  heroImageUrl,
  heroOverlayStrength = 68,
  heroFocalX = 50,
  heroFocalY = 50,
  heroStyle = 'classic',
  brandTheme = {},
  hideSubtitleOnMobile = false,
  hideSecondaryCtasOnMobile = false,
  onExploreDirectory,
  onExploreDeals,
  onExploreEvents,
}) {
  const cityName = String(spotlightName || '').replace(/\s+spotlight$/i, '').trim() || 'My Town';
  const heroHasImage = Boolean(heroImageUrl);

  const overlayStart = (0.38 + (heroOverlayStrength / 100) * 0.5).toFixed(2);
  const overlayMid = (0.28 + (heroOverlayStrength / 100) * 0.44).toFixed(2);
  const overlaySoft = (0.12 + (heroOverlayStrength / 100) * 0.24).toFixed(2);

  const subtitleByStyle = heroStyle === 'editorial'
    ? `Connect with trusted local businesses, neighborhood favorites, and community updates in ${cityName}.`
    : heroStyle === 'minimal'
      ? `Trusted local businesses, mapped for ${cityName}.`
      : DEFAULT_SUBTITLE;

  const headingColor = brandTheme?.heroHeading || '#0F2B5B';
  const bodyColor = brandTheme?.heroBody || '#334155';
  const heroOverlayColor = brandTheme?.heroOverlay || '#F8FAFC';
  const heroWelcomePillBg = brandTheme?.heroWelcomePillBg || '#D1FAE5';

  const hexToRgba = (hex, alpha) => {
    if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
      return `rgba(255, 255, 255, ${alpha})`;
    }

    const red = parseInt(hex.slice(1, 3), 16);
    const green = parseInt(hex.slice(3, 5), 16);
    const blue = parseInt(hex.slice(5, 7), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  };

  const heroShellStyle = heroHasImage
    ? {
      backgroundImage: `url(${heroImageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: `${heroFocalX}% ${heroFocalY}%`,
    }
    : undefined;

  const contentSurfaceStyle = {
    backgroundColor: 'color-mix(in srgb, var(--surface, #F8FAFC) 35%, transparent)',
  };

  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: 'var(--background, #FFFFFF)' }}
    >
      <PublicContainer className="relative !px-0">
        <div
          className="relative overflow-hidden"
          style={heroShellStyle}
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={heroHasImage ? {
              backgroundImage: `linear-gradient(95deg,color-mix(in srgb, ${heroOverlayColor} ${Number(overlayStart) * 100}%, transparent) 0%,color-mix(in srgb, ${heroOverlayColor} ${Number(overlayMid) * 100}%, transparent) 34%,color-mix(in srgb, ${heroOverlayColor} ${Number(overlaySoft) * 100}%, transparent) 56%,transparent 74%)`,
            } : {
              backgroundColor: 'var(--background, #FFFFFF)',
            }}
          />

          <div className="relative p-6 lg:p-10">
            <div className="max-w-[620px] rounded-[2rem] border border-white/30 p-5 shadow-lg backdrop-blur-sm lg:p-7" style={contentSurfaceStyle}>
              <Badge
                variant="success"
                className="mb-4 inline-flex w-fit items-center gap-2 uppercase"
                style={{ backgroundColor: 'var(--primary-soft)', color: 'var(--primary)' }}
              >
                <Heart size={12} className="fill-red-500 text-red-500" />
                Welcome To {cityName}
              </Badge>

              <h1 className="font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-6xl" style={{ color: headingColor }}>
                <span style={{ color: 'var(--primary,#1D4ED8)' }}>Discover the best of</span>{' '}
                <span className="font-logo" style={{ color: 'var(--spotlight,var(--primary,#1D4ED8))', textShadow: '0 1px 0 rgba(255,255,255,.35)' }}>{cityName}</span>
              </h1>

              <p
                className={hideSubtitleOnMobile ? 'mt-4 hidden max-w-xl font-body text-[17px] leading-7 sm:block' : 'mt-4 max-w-xl font-body text-[17px] leading-7'}
                style={{ color: 'var(--primary,#1D4ED8)' }}
              >
                {subtitleByStyle}
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:w-full sm:flex-row sm:flex-wrap">
                <Button
                  variant="ghost"
                  className="w-full gap-3 !bg-[var(--primary)] px-6 !text-[var(--primary-foreground)] hover:!bg-[var(--primary-hover)] hover:!text-[var(--primary-soft)] sm:w-auto"
                  onClick={onExploreDirectory}
                >
                  Explore Businesses
                  <ArrowRight size={16} />
                </Button>
                {!hideSecondaryCtasOnMobile ? (
                  <>
                    <Button
                      variant="ghost"
                      className="w-full gap-3 !bg-[var(--primary-soft)] px-6 !text-[var(--primary)] hover:!bg-[var(--primary-soft)] hover:!text-[var(--primary)] sm:w-auto"
                      onClick={onExploreDeals}
                    >
                      Today&apos;s Deals
                      <Tag size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full gap-3 !bg-[var(--primary-soft)] px-6 !text-[var(--primary)] hover:!bg-[var(--primary-soft)] hover:!text-[var(--primary)] sm:w-auto"
                      onClick={onExploreEvents}
                    >
                      Upcoming Events
                      <Calendar size={16} />
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </PublicContainer>
    </section>
  );
}
